import { Platform, AlertController, App, Events } from 'ionic-angular';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APP_CONFIG, Env, COUNTRY_CONFIG, Region } from '../../environment';
import { HTTP } from '@ionic-native/http';
import { AuthMapper } from '../authorization/auth.mapper';
import { LoadingProvider } from '../loading/loading';
// import { IApiAuthResponseEntity } from '../../model/api/auth/api-auth-response.entity.interface'
import { StorageProvider } from '../storage/storage';
import { RouteProvider } from '../route/route';
import { StorageType } from '../../model/types/storage-type';
import { ProviderUtilities } from '../provider-utilities';
import { LoggerProvider, LoggerLevel, LoggerEvent } from '../logger/logger';
import * as jwt_decode from 'jwt-decode';
import { ConfigProvider } from '../config/config';
import { ErrorCodeType } from '../../model/types/error-code-type';
import { TranslateService } from '@ngx-translate/core';
import { EVENTS_NAME, RET_VAL, RESULT_INFO_PAGES, PAGES, OTP_HEADER_RET_VAL, IP_VALIDATION_REASON_CODE } from "../myria/app-variables";
import { IApiAuthResponseEntity } from '../../model/api/auth/api-auth-response.entity.interface';
import { DeviceProvider } from '../device';
import { SegmentProvider } from '../analytics/segment';
import { AppVersion } from '@ionic-native/app-version';
import { Device } from '@ionic-native/device';
import { Globalization } from '@ionic-native/globalization';
import { AnalyticsEventType, HttpStatusDescriptions } from '../../model/types/analytics.const';
import { LogRocketProvider } from '../../providers/log-rocket/log-rocket';
import { regions } from '../../environment/region/regions';
import { readAutomationCountryOverride } from '../myria/automation-country';
import { InAppBrowser } from '@ionic-native/in-app-browser'
import { VueBridgeContainerHelpers, VueBridgeState } from '../vue/vue-helpers';
import { User } from '../../model/myria/app/user';
import { APIGuardProvider } from '../../providers/http/api-guard';
import { FeatureFlagTypes, FlagResolutionSource } from '../../model/types/feature-flag-types';
import { brandings } from '../../environment/branding/brandings';

export const DEFAULT_HEADERS = {
  'Client-Type': 'App',
  'AppType': '1' // 0 should be MyRia and 1 OneApp
}

export const RefreshUserDetails = {
  'None': 'None',
  'Basic': 'Basic',
  'Full': 'Full', // also includes identification
}

declare let navigator;

@Injectable()
export class HttpProvider {
  static sessionResponse: any;
  static authToken: string;
  static refreshToken: string;
  private translationCheckTimeoutId: any = null;
  static deviceToken: string;
  static authExpiresInSeconds = 120;
  static authCountry: string;
  static currentLanguage: string = 'en-US'; // Default to en-US until properly initialized
  static parsedAuthToken: any = null;
  static parsedApiToken: any = null;
  static disableCaptcha: boolean = true;
  static readyResolve;
  static readyPromise: any = new Promise((resolve) => { HttpProvider.readyResolve = resolve; });
  static relogging = false;
  /** A relogin is being shown by RMT5: its requests are the login flow and must not wait. */
  static vueReloginInProgress = false;
  static user: User;
  static featureFlags = {};
  static featureFlagsLog = '';
  static featureFlagsResolvedLog = '';
  static featureFlagsResolvedCount = 0;
  static featureFlagsUser = {};
  static featureFlagsCountry = {
    country: ''
  };
  static featureFlagsCountryPending = false;
  static featureFlagCountryPendingPromise = null;

  pendingUpdate: boolean = false;
  versionFileURL = '/assets/data/version.json';
  initPromise: Promise<any>;
  headers;
  cultureCode: string = APP_CONFIG.region.defaultLanguageCulture;
  blockPromise;
  static authCountryPromise;
  static authCountryResolve;
  static sessionCountry;
  static betaVersion = '5.0.0';
  static version: string = '5.0.0';
  static minVersion = '5.0.0';
  static requiredMinVersion: string | null = null;
  static sendLogExtra = '';
  static lastActivity = Date.now();
  static lastActivityTimer;
  static lastActivityTimeout;
  static inactivityResolve;
  static inactivityPromise;
  static refreshInProgress: Promise<boolean> | null = null;
  static publicTokenRenewalInProgress: Promise<any> | null = null;
  refreshTimeout;
  lastRefresh = new Date();
  secondsOffset = 0;

  static skipRefreshMaxLimit: boolean = false;
  static forceRefreshTime: number = 0;
  static isTestVersion: boolean = false;

  constructor(
    public app: App,
    public events: Events,
    private platform: Platform,
    private httpNative: HTTP,
    private http: HttpClient,
    private alertCtrl: AlertController,
    private storageProvider: StorageProvider,
    private loadingProvider: LoadingProvider,
    private routeProvider: RouteProvider,
    private translateService: TranslateService,
    private deviceProvider: DeviceProvider,
    private analytics: SegmentProvider,
    private appVersion: AppVersion,
    private globalization: Globalization,
    private logRocketProvider: LogRocketProvider,
    private device: Device,
    private iab: InAppBrowser,
    private apiGuardProvider: APIGuardProvider) {
      HttpProvider.authCountryPromise = new Promise((resolve) => {
        HttpProvider.authCountryResolve = resolve;
      });

    HttpProvider.checkLastActivity();

  if (this.isTestVersion())
      HttpProvider.sendLogExtra = ` (${HttpProvider.minVersion}) `;
    this.initAppConfigRegions();
  }

  static checkLastActivity() {
    if (this.lastActivityTimer)
      window.clearTimeout(this.lastActivityTimer);
    this.setLastActivity();
    this.lastActivityTimer = window.setTimeout(() => {
      this.setLastActivity();
      this.checkLastActivity();
    }, 1000);
  }

  static setLastActivity() {
    const passed = Date.now() - this.lastActivity;
    if (passed >= 30000 && HttpProvider.hasAuthCustomerId()) {
      // since we can't influence the order when resume is called in auth.ts when we notice that the application has been away we will wait for it
      // to be on the safe side we have a 5 seconds timeout if somehow resume was not called
      if (HttpProvider.inactivityPromise)
        LoggerProvider.info(this, 'Seems application was away (' + passed + ' ms) - waiting for resume is already in progress');
      else {
        LoggerProvider.info(this, 'Seems application was away (' + passed + ' ms) - waiting for resume');
        HttpProvider.inactivityPromise = new Promise((resolve) => { HttpProvider.inactivityResolve = resolve; });
      }
      this.setLastActivityTimeout();
    }
    this.lastActivity = Date.now();
  }

  static setLastActivityTimeout() {
    if (this.lastActivityTimeout)
      window.clearTimeout(this.lastActivityTimeout);
    this.lastActivityTimeout = window.setTimeout(() => {
      if (HttpProvider.inactivityResolve)
        HttpProvider.inactivityResolve(true);
      HttpProvider.inactivityPromise = null;
      HttpProvider.lastActivityTimeout = null;
      LoggerProvider.info(this, 'Timeout while waiting for resume');
    }, 5000);
  }

  public checkRefresh() {
    if (this.refreshTimeout)
      this.refresh();
  }

  public isNewerVersion (oldVer, newVer, allowEqual = false) {
    const oldParts = oldVer.split('.')
    const newParts = newVer.split('.')
    for (var i = 0; i < newParts.length; i++) {
      const a = ~~newParts[i] // parse int
      const b = ~~oldParts[i] // parse int
      if (a > b) return true
      if (a < b) return false
    }
    if (allowEqual)
      return true;
    return false
  }

  private checkWaitForReady(url, isBridgeRequest = false) {
    let waitForReady = true;
    if (HttpProvider.relogging && url) {
      waitForReady = false;
      // RMT5 validates its login OTP through native HTTP, so the URL stands in for the bridge flag
      const isVueReloginRequest = HttpProvider.vueReloginInProgress && (isBridgeRequest || url.toLowerCase().indexOf('/validateotp') > 0);
      if (isVueReloginRequest)
        LoggerProvider.info(this, 'Skip readyPromise for RMT5 relogin');
      else if (url.indexOf('/signin') > 0)
        LoggerProvider.info(this, 'Skip readyPromise for relogin');
      else if (url.indexOf('/auth/captcha') > 0)
        LoggerProvider.info(this, 'Skip readyPromise for captcha');
      else if (url.indexOf('/log') > 0)
        LoggerProvider.info(this, 'Skip readyPromise for log');
      else
        waitForReady = true;
    }
    return waitForReady;
  }

  // Bridge requests must not wait behind a connection alert the webview hides.
  private async checkConnection(log, url, isBridgeRequest = false) {
    const relogging = HttpProvider.relogging;
    const waitForReady = this.checkWaitForReady(url, isBridgeRequest);
    if (waitForReady) {
      const result = await HttpProvider.readyPromise;
      if (!result && relogging) {
        LoggerProvider.info(this, 'Relogin failed hence cancel request ' + url);
        return false;
      } else if (relogging && url.indexOf('/refresh') > 0) {
        // doesn't make sense to send an old refresh which probably contains an old refresh token after relogin
        LoggerProvider.info(this, 'Relogin finished - cancel refresh ' + url);
        return false;
      }
      if (result && url && url.endsWith('/refresh'))
      {
        LoggerProvider.info(this, 'Cancel old refresh request after relogin ' + url);
        return false;
      }
    }

    if (isBridgeRequest)
      return true;

    try {
      let checkResolve;
      const promise = new Promise((resolve) => {
        checkResolve = resolve;
      });
      this.processConnectionCheck(log, url, checkResolve, promise);
      await promise;
      if (this.blockPromise === promise) {
        this.blockPromise = null;
        this.logConnectionIssue(log, 'Connection is back', {url:url, connectionType: navigator.connection.type});
      }
    }
    catch (ex) {
      LoggerProvider.warning(log, LoggerEvent.ConnectionError, 'Exception during waiting for connection', ex);
    }
    return true;
  }

  public async waitForConnection() {
    return new Promise<void>(async(resolve) => {
      if (this.blockPromise) {
        try {
          await this.blockPromise;
          resolve();
        }
        catch (ex) {
          LoggerProvider.warning('', LoggerEvent.ConnectionError, 'Exception during waiting for block promise ?!', ex);
        }
      } else {
        resolve();
      }
    })
  }

  async processConnectionCheck(log, url, resolve, promise) {
    if (this.blockPromise) {
      LoggerProvider.debug(log, 'Connection on hold');
      try {
        await this.blockPromise;
      }
      catch (ex) {
        LoggerProvider.warning(log, LoggerEvent.ConnectionError, 'Exception during waiting for block promise ?!', ex);
      }
      LoggerProvider.debug(log, 'Connection available again');
    }

    let type, online;
    if (typeof navigator.connection !== 'undefined')
      type = navigator.connection.type;
    online = navigator.onLine;

    if (this.isConnectionCheckable(url) && (type === 'none' || !online)){
      this.blockPromise = promise;
      this.logConnectionIssue(log, 'No Connection', {url:url, connectionType: type});
      this.showNoConnectionAlert(resolve);
    }
    else
      resolve();
  }

  private isConnectionCheckable(url, status = null) {
    if (APP_CONFIG.phpApiBaseUrl && APP_CONFIG.phpApiBaseUrl.length > 0 && url.indexOf(APP_CONFIG.phpApiBaseUrl) >= 0)
      return false;
    if (url.indexOf('/log') >= 0)
      return false;
    if(url.indexOf('ignore_connection_errors') >= 0)
      return false;
    if (url.indexOf('/api/OTP/SecureResource/') >= 0 && APP_CONFIG.env !== Env.PROD) // TODO !!!!!!!!!!!!!!!!!!!!!!!!!! temporary !
      return false;
    if (url.indexOf('/api/Account/EmailValidation') >= 0)
      return false;
    if (url.indexOf('/api/auth/captcha') >= 0 && APP_CONFIG.env !== Env.PROD) // TODO !!!!!!!!!!!!!!!!!!!!!!!!!! temporary !
      return false;
    if (url.indexOf('/api/Customer/v2') >= 0) // just to be on the safe side if lite registration somehow is repeated it would throw a 403 which would logout the user - so ignore it
      return false;
    if (url.indexOf('/api/Customer/distinct-recent-recipients') >= 0) // TODO !!!!!!!!!!!!!!!!!!!!!!!!!! temporary !
      return false;
      if (url.indexOf('/api/Account/User/IsRegistered') >= 0) // TODO !!!!!!!!!!!!!!!!!!!!!!!!!! temporary !
      return false;
    if (url.indexOf(APP_CONFIG.ITERABLE_INAPP_API) >= 0) // we noticed Iterable
      return false;
    if (url.indexOf('/base64EncodedMainRequest') >= 0) // wallet card processor (EFT) - its WAF can return 403 which must not log the user out
      return false;

    // special case in location-list - there are locations like "Anywhere in country" (i.e. Philippines) where we get a 404 when we try to get location details
    if (status == 404 && url.indexOf('/agent/locations') > 0)
      return false;
    if (status == 404 && url.indexOf('/Account/Verification/Token/IsValid') > 0)
      return false;

    return true;
  }

  private async showNoConnectionAlert(resolve){
    let type, online;
    if (typeof navigator.connection !== 'undefined')
      type = navigator.connection.type;
    else
      online = navigator.onLine;

    if ((type && type !== 'none') || online) {
      await this.routeProvider.waitForTransitions();
      const alert = this.alertCtrl.create({
        subTitle: this.translateService.instant('page.activityPage.connectionStabilizeMessage'),
        enableBackdropDismiss: false
      });
      alert.present();

      // if connection is available again wait a few seconds to ensure that it says the same and is stable
      // (i.e. when WLAN and LTE get available at the same time, first LTE kicks in and then a few seconds later WLAN which can cause a connection abort which would  be returned as a timeout)
      window.setTimeout(() => {
        alert.dismiss();
        let currentType, currentOnline;
        if (typeof navigator.connection !== 'undefined')
          currentType = navigator.connection.type;
        currentOnline = navigator.onLine;
        if (currentType !== type || !currentOnline)
          this.showNoConnectionAlert(resolve);
        else
          resolve();
      }, ConfigProvider.settings.reconnectTimeoutMs);
    }
    else {
      await this.isTranslationReady();
      await this.routeProvider.waitForTransitions();
      this.loadingProvider.hide();
      const alert = this.alertCtrl.create({
        title: this.translateService.instant('global.serviceUnavailableHeader'),
        subTitle: this.translateService.instant('global.serviceUnavailableMessage'),
        enableBackdropDismiss: false,
        buttons: [{
          text: this.translateService.instant('global.tryAgainButton'),
          handler: () => {
            // delay one frame to make it smoother
            window.requestAnimationFrame(() => {
              this.showNoConnectionAlert(resolve);
            });
          }
        }]
      });
      alert.present();
    }
  }

  public isTranslationReady(): Promise<void>{
    return new Promise(async(resolve) => {
      // Cancel any existing translation check
      if (this.translationCheckTimeoutId) {
        clearTimeout(this.translationCheckTimeoutId);
        this.translationCheckTimeoutId = null;
      }

      const lang = await this.storageProvider.get(StorageType.Language);
      let isResolved = false;
      const safeResolve = () => {
        if (!isResolved) {
          isResolved = true;
          // Clear timeout reference when resolved
          if (this.translationCheckTimeoutId) {
            clearTimeout(this.translationCheckTimeoutId);
            this.translationCheckTimeoutId = null;
          }
          resolve();
        }
      };
      this.isLanguageSet(lang, safeResolve);
    })
  }

  public getTranslateLang(lang = null) {
    if (!lang)
      lang = this.translateService.currentLang;
    if (APP_CONFIG.featureFlags.lokaliseContent && lang)
      lang = lang.replace('_', '-');
    return lang;
  }

  private isLanguageSet(lang:string, resolve, retryCount = 0){
    const currentLang = this.getTranslateLang();

    // checking if the translation strings are loaded and at the same time the current language
    // matches the language stored by the user or set to null (no prefered language stored)
    const transStr = this.translateService.instant('page.activityPage.connectionProblemMessage');
    if (transStr.indexOf('NOT FOUND') === -1 && (lang === currentLang || lang === null)) {
      this.translationCheckTimeoutId = null;
      resolve();
      return;
    }
    else if (retryCount > 25) {
      this.translationCheckTimeoutId = null;
      resolve();
      return;
    }
    else {
      this.translationCheckTimeoutId = setTimeout(()=>this.isLanguageSet(lang, resolve, retryCount + 1),200);
    }
  }

  private logConnectionIssue(log, message, details){
    LoggerProvider.log(log, LoggerLevel.Error, LoggerEvent.ConnectionError, message, details);
  }

  private async prepareExternHttpNative(url, forceSslPinning = false) {
    if ((this.isCstUrl(url) || forceSslPinning) && APP_CONFIG.env === Env.PROD) {
      // Use certificates from the src/certificates folder
      this.httpNative.setSSLCertMode('pinned');
      this.httpNative.setRequestTimeout(ConfigProvider.settings.connectionTimeout);
    }
    else {
      await this.httpNative.setSSLCertMode('nocheck');
    }
  }

  private prepareHttpNative(url: string = "") {
    this.httpNative.setRequestTimeout(ConfigProvider.settings.connectionTimeout);
    if (url.endsWith('/order')) {
      this.httpNative.setRequestTimeout(ConfigProvider.settings.orderConnectionTimeout);
    }
    this.httpNative.setDataSerializer('json');
    if (APP_CONFIG.env === Env.PROD) {
      // Use certificates from the src/certificates folder
      this.httpNative.setSSLCertMode('pinned');
    }
    else {
      this.httpNative.setSSLCertMode('nocheck');
    }
  }

  private handleNativeResponse(response, resolve, reject, parseFunction, log) {
    if (response.headers) // TODO && requireAuth !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      this.checkBearer(response.headers['bearer']);

    this.captureRequiredMinVersion(response.headers);
    this.apiGuardProvider.parseResponseHeaders(response.headers);

    let success = false;
    let data = response.data;
    try {
      if (!this.checkResponseActions(data))
        return;

      if (response.status >= 200 && response.status < 300) {

        if (parseFunction && parseFunction.fullResponse) {
          response.responseData = data;
          data = parseFunction(response, response.headers);
          if (data)
            success = true;
        }
        else if (parseFunction && data) {
          if (parseFunction.headers)
            data = parseFunction(data, response.headers);
          else
            data = parseFunction(data);
          if (data)
            success = true;
        }
        else
          success = true;
      }
    }
    catch (ex) {
      LoggerProvider.exception(log, ex, LoggerEvent.CommunicationError, 'Exception at parsing the response');
    }

    if (success) {
      this.populateRiaSession(response);
      resolve(data);
    }
    else {
      data = response.data;
      if (!data)
        data = response;
      let parsed = 'Unable to parse response';
      try {
        parsed = JSON.stringify(data, null, 2);
      }
      catch (ex) {
      }
      LoggerProvider.error(log, LoggerEvent.CommunicationError, 'Invalid response', parsed);
      reject(data);
    }
  }

  public routeToStart(forceToStartPage = false) {
    if (!this.routeProvider.isStartPage() || forceToStartPage) {
        LoggerProvider.info(this, 'Route to start');
        // Ensure any Vue bridge iframe overlay is hidden before navigating
        try {
          const container = document.getElementById('vue-container');
          if (container) {
            VueBridgeContainerHelpers.setContainerHidden(container as HTMLElement);
          }
        } catch (ex) {
          LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Failed to hide Vue container on routeToStart', ex);
        }
        this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage, {redirected: false});
    }
  }

  public isTimeoutStatus(status) {
    // -4 = connection timeout
    if (status === -4 || status === 1 || status === 504)
      return true;
    return false;
  }

  private handleErrorStatus(status, log, url, err, isBridgeRequest = false) {
    if (!this.isConnectionCheckable(url, status)) {
      if (status === 403 && !isBridgeRequest && this.isIpLocationFailed(err, url))
        err['ipRestrictionShown'] = true;
      return;
    }

    // in QA environment we need to go through ZScaler otherwise we will get an error that we're not allowed like that:
    // "error": "\n <html>\n <head>\n <title>Access Denied.</title>\n </head>\n <body>\n The IP address (95.115.82.120 (DE)) is not authorized.\n </body>\n </html> \n \t"
    // Since that is not a 403 in our sense, we also shouldn't treat it like that and will return here
    // (otherwise if there was a temporary ZScaler problem or accidentely turned off the user would be logged out)
    if (APP_CONFIG.env !== Env.PROD && status === 403 && err && err.error) {
      const regex = /The IP address \(.*\) is not authorized\./;
      if (regex.test(err.error)) {
        LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Ignored 403 error since it is caused by missing white-listing', err.error);
        return;
      }
    }
    const isSigninEndpoint = url && /\/api\/auth\/(fingerprint\/)?(v\d+\/)?signin$/i.test(url);

    if (status === 401 && this.isRemittanceBased(url) && !this.initPromise && !isSigninEndpoint)
      this.resetAuthToken();

    if (!isBridgeRequest && this.isTimeoutStatus(status) && url !== `${APP_CONFIG.remittanceApiBaseUrl}order`) // exclude order URL since it has an own timeout handling
      this.showConnectionProblemAlert(log, this.translateService.instant('global.errorTimeoutHeader'), this.translateService.instant('global.errorTimeoutMessage'), this.translateService.instant('global.tryAgainButton'));

    switch(status) {
      case -1:
      case 0:
        if (isBridgeRequest) break;
        if (!navigator.onLine) this.showConnectionProblemAlert(log, this.translateService.instant('global.serviceUnavailableHeader'), this.translateService.instant('global.serviceUnavailableMessage'), this.translateService.instant('global.tryAgainButton'));
        else this.showConnectionProblemAlert(log, this.translateService.instant("global.errorGeneralHeader"), this.translateService.instant("global.errorGeneralMessage"), this.translateService.instant('global.gotItButton'));
        break;
      case 301:
        this.manageErrorAppVersion();
        break;
      case 401:
        if (isSigninEndpoint) break;
        this.manageErrorUnauthorized(err, url);
        break;
      case 403:
        this.manageErrorUnauthorized(err, url, isBridgeRequest);
        break;
      case 500:
        this.manageInternalServerError(log, url, isBridgeRequest);
    }
  }

  manageErrorAppVersion() {
    this.events.publish(EVENTS_NAME.UPDATE_PAGE);
  }

  private isIpLocationFailed(errorObject: any, url: string = '', isBridgeRequest = false) {
    if (!errorObject) return false;

    let err = errorObject.error;

    if (err !== null && typeof err === 'string') {
      try { err = JSON.parse(err); } catch (e) { return false; }
    }

    if (errorObject.status !== 403 || !err || err.Allowed !== false || !err.ReasonCode) return false;

    const reasonCode = err.ReasonCode;
    const stringContainsIpAddress = /ip address/i.test(err.Message || '');
    const isLoginIp = stringContainsIpAddress && reasonCode === IP_VALIDATION_REASON_CODE.LOGIN_IP_RESTRICTED;

    if (isLoginIp || reasonCode === IP_VALIDATION_REASON_CODE.REGISTRATION_IP_RESTRICTED) {
      return isBridgeRequest || this.showIpRestriction(isLoginIp);
    }

    if ([IP_VALIDATION_REASON_CODE.REGISTRATION_LOCAL_IP_REQUIRED, IP_VALIDATION_REASON_CODE.REGISTRATION_COUNTRY_NOT_ALLOWED].includes(reasonCode)) {
      const isResourceApi = /api\/Resources/i.test(url);

      if (!isResourceApi || this.routeProvider.hasPage(this.routeProvider.pages.RlUserCountryPage)) {
        return isBridgeRequest || this.showRegistrationIpRestriction(reasonCode);
      }
    }

    if (reasonCode >= 2 && reasonCode <= 9) {
      return isBridgeRequest || this.showGenericRestrictionError();
    }

    return false;
  }

  private showIpRestriction(isLoginIp: boolean) {
    const prefix = isLoginIp ? 'page.login.error.restrictedLocation' : 'page.registration.error.restrictedLocation';
    const brand = APP_CONFIG.branding.partnerName || 'Ria';
    this.routeProvider.setRoot(this.routeProvider.pages.ModalAlertPage, {
      img: 'assets/imgs/warning-circle-background.svg',
      showFullPage: true,
      hideHeader: true,
      title: this.translateService.instant(`${prefix}.title`),
      subtitle: this.translateService.instant(`${prefix}.bodyBrand`, { brand }),
      primaryButtonText: this.translateService.instant('global.goBackButton'),
      primaryAction: () => { this.routeToStart(); }
    });
    return true;
  }

  private showRegistrationIpRestriction(reasonCode: number) {
    let prefix = 'page.registration.error.restrictedLocation';
    let bodySuffix = 'bodyBrand';

    if (reasonCode === IP_VALIDATION_REASON_CODE.REGISTRATION_LOCAL_IP_REQUIRED) {
      const localIpPrefixMap = {
        'UK': 'page.registration.error.requiresLocalIpUk',
        'CH': 'page.registration.error.requiresLocalIpCh',
      };
      const localIpPrefix = localIpPrefixMap[HttpProvider.authCountry];
      if (localIpPrefix) {
        prefix = localIpPrefix;
        bodySuffix = 'body';
      }
    }

    const brand = APP_CONFIG.branding.partnerName || 'Ria';

    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      img: 'assets/imgs/warning-circle-background.svg',
      title: this.translateService.instant(`${prefix}.title`),
      subtitle: this.translateService.instant(`${prefix}.${bodySuffix}`, { brand }),
      primaryButtonText: this.translateService.instant('page.registration.error.action.selectDifferentCountry'),
      primaryAction: () => { this.routeToStart(); }
    });
    return true;
  }

  private showGenericRestrictionError() {
    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      title: this.translateService.instant('global.errorGeneralHeader'),
      subtitle: this.translateService.instant('global.errorGeneralMessage'),
      primaryButtonText: this.translateService.instant('global.gotItButton')
    });
    return true;
  }

  manageErrorUnauthorized(err, url, isBridgeRequest = false) {
    LoggerProvider.info(this, 'Unauthorized response');

    if (ConfigProvider.settings.trackNetworkEvents)
    this.analytics.trackEvent({ eventName: AnalyticsEventType.NetworkError, analyticsData: {
      error: 'unauthorized',
      url: url
    }});

    let nav = this.app.getActiveNav();

    if (err.status === 403 && this.isIpLocationFailed(err, url, isBridgeRequest)) {
      return;
    }

    const header = this.platform.is('cordova') ? (err.headers['RetVal']?err.headers['RetVal']:err.headers['retval']) : err.headers.get('RetVal');

    if(header == RET_VAL.CUSTOMER_ON_HOLD) {
      nav.setRoot(PAGES.RESULT_INFO, { retVal: header, page: RESULT_INFO_PAGES.NOT_DEFINED });
    }
    else if(header == OTP_HEADER_RET_VAL.ACCOUNT_BLOCKED) {
      this.events.publish(EVENTS_NAME.LOGOUT, {page: PAGES.RESULT_INFO, params: {page: RESULT_INFO_PAGES.OTP_ACCOUNT_BLOCKED}});
    }
    else if(HttpProvider.authToken) {
      if (VueBridgeState.isUserInsideVueBridge) {
        LoggerProvider.info(this, 'Skipping routeToStart for unauthorized response while inside Vue bridge');
      } else {
        this.routeToStart();
      }
    }
    else {
      if (VueBridgeState.isUserInsideVueBridge) {
        LoggerProvider.info(this, 'Skipping logout for unauthorized response while inside Vue bridge');
      } else {
        this.events.publish(EVENTS_NAME.LOGOUT);
      }
    }
  }

  manageInternalServerError(log, url, isBridgeRequest = false) {
    if (ConfigProvider.settings.trackNetworkEvents)
      this.analytics.trackEvent({ eventName: AnalyticsEventType.NetworkError, analyticsData: {
        error: 'service_unavailable',
        url: url
      }});
    if (isBridgeRequest) return;
    window.setTimeout(() => {
      const alerts = document.getElementsByClassName("alert-wrapper");
      if (!alerts || alerts.length > 0) {
        return;
      }
      this.showConnectionProblemAlert(log, this.translateService.instant("global.errorGeneralHeader"), this.translateService.instant("global.errorGeneralMessage"), this.translateService.instant('global.gotItButton'));
    }, 100);
  }

  private handleNativeError(err, reject, log, url, isBridgeRequest = false) {
    if (this.isConnectionCheckable(url, err.status)) {
      if (err.status === -1 || err.status === 0 || err.status === -4) { // connection aborted (-4 is actually saying request time out but it happens after a few seconds so it is more likely some kind of abort)
        err['errorCode'] = ErrorCodeType.ConnectionError;
        err['errorMessage'] = this.translateService.instant('page.sendFailedPage.sendFailedContent');

        if (ConfigProvider.settings.trackNetworkEvents)
          this.analytics.trackEvent({ eventName: AnalyticsEventType.NetworkError, analyticsData: {
            error: 'abort',
            url: url
          }});
      }
      else if (err.status === 1) { // timeout
        err['errorCode'] = ErrorCodeType.ConnectionError;
        err['errorMessage'] = this.translateService.instant('page.sendFailedPage.sendFailedContent');

        if (ConfigProvider.settings.trackNetworkEvents)
          this.analytics.trackEvent({ eventName: AnalyticsEventType.NetworkError, analyticsData: {
            error: 'timeout',
            url: url
          }});
      }
    }

    this.handleErrorStatus(err.status, log, url, err, isBridgeRequest);
    if (err.headers) {
      this.checkBearer(err.headers['bearer']);
      this.apiGuardProvider.parseResponseHeaders(err.headers);
    }
    if (err.error !== null && typeof err.error === 'string') {
      try {
        err.error = JSON.parse(err.error);
      }
      catch (ex) {
      }
    }
    err = this.parseError(err);
    reject(err);
  }

  showConnectionProblemAlert(log, title, message, button) {
    if (!title)
      title = this.translateService.instant('global.serviceUnavailableHeader')
    this.logConnectionIssue(log, title, null);

    if (!this.blockPromise) {
      this.blockPromise = new Promise<void>((resolve) => {
        this.routeProvider.waitForTransitions().then(() => {
          // if loader is visible hide it otherwise it would block the alert if alert has been resolved we can show it again
          const showLoading = this.loadingProvider.isLoading();
          this.loadingProvider.hide();
          this.loadingProvider.hideCalledRecently = false;

          const alert = this.alertCtrl.create({
            title: title,
            subTitle: message,
            enableBackdropDismiss: false,
            buttons: [{
              text: button ? button : this.translateService.instant('global.gotItButton'),
              role: 'cancel',
              handler: () => {
                if (showLoading && !this.loadingProvider.hideCalledRecently) // only show again if hide hasn't been called meanwhile
                  this.loadingProvider.show();
                this.blockPromise = null;
                resolve();
              }
            }]
          });
          alert.present();
        });
      });
    }
  }

  hasConnectionProblem() {
    if (this.blockPromise)
      return true;
    return false;
  }

  checkResponseActions(data) {
    if (data && data.action && data.actionValue) {
      if (!this.checkResponseAction(data.action, data.actionValue, data.actionTitle))
        return false;
    }
    if (data && data.actions) {
      for (let i=0; i < data.actions.length; i++) {
        const action = data.actions[i];
        if (action && action.action && action.actionValue) {
          if (!this.checkResponseAction(action.action, action.actionValue, action.actionTitle))
            return false;
        }
      }
    }
    return true;
  }

  private handleResponse(response, resolve, reject, parseFunction, log) {
    //if (requireAuth) TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      this.checkBearer(response.headers.get('bearer'));

    this.captureRequiredMinVersion(response.headers);

    let data = response.body;
    let success = false;
    try {
      if (!this.checkResponseActions(data))
        return;

      if (parseFunction && parseFunction.fullResponse) {
        response.responseData = data;
        data = parseFunction(response);
        if (data)
          success = true;
      }
      else if (parseFunction && data) {
        if (parseFunction.headers) {
          const headers = {};
          if (response.headers) {
            const keys = response.headers.keys();
            for (let i=0; i < keys.length; i++) {
              const key = keys[i];
              headers[key] = response.headers.get(key);
            }
          }
          data = parseFunction(data, headers);
        }
        else
          data = parseFunction(data);
        if (data)
          success = true;
      }
      else
        success = true;
    }
    catch (ex) {
      LoggerProvider.exception(log, ex, LoggerEvent.CommunicationError, 'Exception at parsing the response');
    }

    if (success) {
      this.populateWebRiaSession(response);
      resolve(data);
    } else {
      reject(response.body);
    }
  }

  private handleError(err, reject, log, url, isBridgeRequest = false) {
    if (this.isConnectionCheckable(url) && err.status <= 0) {
      err['errorCode'] = ErrorCodeType.ConnectionError;
      err['errorMessage'] = this.translateService.instant('page.sendFailedPage.sendFailedContent');
    }
    this.handleErrorStatus(err.status, log, url, err, isBridgeRequest);
    if (err.headers)
      this.checkBearer(err.headers.get('bearer'));
    err = this.parseError(err);
    reject(err);
  }

  parseError(err) {
    if (err.error && err.error.errors && err.error.errors.length > 0) {
      err['errorCode'] = err.error.errors[0].errorCode;
      err['errorKey'] = err.error.errors[0].key;
      err['errorMessage'] = err.error.errors[0].friendlyMessage;
      err['errorCurrent'] = err.error.errors[0];
    }

    if (!this.checkResponseActions(err))
      return;
    if (err['errorCode'] && err['errorMessage']) {
      if (!this.checkResponseAction(err['errorCode'], err['errorMessage'], err['errorTitle']))
        return;
    }

    return err;
  }

  private checkResponseAction(action, value, title) {
    if (action === 'ExitApp') {
      const alert = this.alertCtrl.create({
        title: title,
        subTitle: value,
        enableBackdropDismiss: false,
        buttons: [{
          text: 'Exit',
          handler: () => {
            this.platform.exitApp();
          }
        }]
      });
      alert.present();
    }
    else if (action === 'MessageBox') {
      const alert = this.alertCtrl.create({
        title: title,
        subTitle: value,
        buttons: [{
          text: 'OK'
        }]
      });
      alert.present();
    }
    else if (action === 'RequirePassword') {
      const alert = this.alertCtrl.create({
        title: title,
        subTitle: value,
        inputs: [
          {
            name: 'password',
            placeholder: 'Password',
            type: 'password'
          }
        ],
        buttons: [{
          text: 'Verify',
          handler: (data) => {
            this.checkPassword(data.password);
          }
        }]
      });
      alert.present();
    }
    else if (action === 'Logout') {
      LoggerProvider.info(this, 'Response action logout');
      this.resetAuthToken();
      this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
    }
    else if (action === 'ForceUpdate' && !this.pendingUpdate){
      this.pendingUpdate = true;
      this.forceUpdate();
    }
    else if (action === 'DisableCaptcha') {
      if (!HttpProvider.disableCaptcha)
        LoggerProvider.info(this, 'Disabled captcha');
      HttpProvider.disableCaptcha = true;
    }
    else if (action === 'Analytics') {
      this.analytics.trackEvent({ eventName: title, analyticsData: value});
    }
    else if (action === 'PagePush')
      this.routeProvider.push({ page: value });
    else if (action === 'PageSetRoot')
      this.routeProvider.push({ page: value });
    else if (action === 'PagePop')
      this.routeProvider.pop();
    else if (action === 'PagePopTo')
      this.routeProvider.popTo({ page: value });
    else if (action === 'PageReplace')
      this.routeProvider.replace({ page: value });
    else if (action === 'LogCritical')
      LoggerProvider.critical(this, LoggerEvent.ResponseAction, value);
    else if (action === 'LogError')
      LoggerProvider.error(this, LoggerEvent.ResponseAction, value);
    else if (action === 'LogWarning')
      LoggerProvider.warning(this, LoggerEvent.ResponseAction, value);
    else if (action === 'LogInfo')
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.ResponseAction, value);
    else if (action === 'LogNotice')
      LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.ResponseAction, value);
    else if (action === 'LogTrace')
      LoggerProvider.log(this, LoggerLevel.Trace, LoggerEvent.ResponseAction, value);
    else if (action === 'LogDebug')
      LoggerProvider.log(this, LoggerLevel.Debug, LoggerEvent.ResponseAction, value);
    else if (action === 'Stop') {
      LoggerProvider.warning(this, LoggerEvent.ResponseStopAction, 'Stopped by action');
      return false;
    }

    return true;
  }

  private async checkPassword(password) {
    this.loadingProvider.show();
    const username = await this.storageProvider.get(StorageType.LastLogin);
    const payload = {
      username: username,
      password: password
    }
    try {
      const url = `${APP_CONFIG.API_URL}/api/auth/signin`;
      await this.post(url, payload, (response: any) => {
        const signin = AuthMapper.fromSigninEntity(response.Value);
        this.storageProvider.set(StorageType.LastLogin, payload.username);
        return signin;
      });
    }
    catch (ex) {
      this.loadingProvider.hide();
    }
  }

  private checkBearer(bearer) {
   // if (APP_CONFIG.region.oldRemittanceLogin && bearer && bearer !== 'null') {
      // in a race condition we can login and then get the response from a previous call before the login which would then overwrite our
      // authorized token with an unauthorized token
    //  if (HttpProvider.authToken && HttpProvider.hasAuthCustomerId() && !HttpProvider.hasAuthCustomerId(bearer)) {
    //    LoggerProvider.info(this, 'Racing condition for bearer - skip new bearer');
    //    return;
    //  }
     // this.setAuthToken(bearer, null);
    //}
  }

  setAuthToken(signinAuthToken) {
    let authToken = null;
    let refreshToken = null;
    if (signinAuthToken) {
      authToken = signinAuthToken.jwt;
      refreshToken = signinAuthToken.refresh;
      if (signinAuthToken.expiresInSeconds)
        HttpProvider.authExpiresInSeconds = signinAuthToken.expiresInSeconds;
    }
    HttpProvider.authToken = authToken;
    if (authToken)
      this.storageProvider.set(StorageType.AuthToken, authToken);
    else
      this.storageProvider.remove(StorageType.AuthToken);
    HttpProvider.refreshToken = refreshToken;
    if (refreshToken)
      this.storageProvider.set(StorageType.RefreshToken, refreshToken);
    else {
      this.storageProvider.remove(StorageType.RefreshToken);
      HttpProvider.refreshInProgress = null;
      if (this.refreshTimeout) {
        LoggerProvider.info(this, 'Logout - clear refresh timeout');
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = null;
      }
    }

    if (authToken) {
      HttpProvider.parsedAuthToken = jwt_decode(authToken);
      // calculating the time offset in seconds between the device and the server to prevent the forced
      // logout issue that could happen from having the device time in the future close to the token expiry
      this.secondsOffset = HttpProvider.parsedAuthToken.nbf - Math.trunc(Date.now() / 1000);
      if (!HttpProvider.parsedAuthToken)
        throw new Error('Failed to parse auth token');
      if (!HttpProvider.parsedAuthToken.apiToken)
        throw new Error('Missing API token');
      HttpProvider.parsedApiToken = jwt_decode(HttpProvider.parsedAuthToken.apiToken);
      if (!HttpProvider.parsedApiToken)
        throw new Error('Failed to parse API token');
    } else {
      HttpProvider.parsedAuthToken = null;
      HttpProvider.parsedApiToken = null;
    }
    LoggerProvider.user = this.getAuthEmail();

    this.setAuthTokenFeatureFlags(signinAuthToken, HttpProvider.parsedAuthToken, 'after set authorization token');

    // TEMPORARY - can be removed later when feature flags come again from login/refresh or when country gets changed
    if (HttpProvider.parsedAuthToken) {
      const country = this.getSessionCountryFromToken(HttpProvider.parsedAuthToken);
      if (country !== HttpProvider.authCountry) {
        LoggerProvider.info(this, `Session country ${country} is different than current country ${HttpProvider.authCountry}`);
        HttpProvider.featureFlagsCountryPending = true;
        this.checkFeatureFlagsCountryPending();
      }
    }
  }

  async checkFeatureFlagsCountryPending() {
    if (HttpProvider.featureFlagsCountryPending) {
      if (!HttpProvider.featureFlagsCountry.country || HttpProvider.featureFlagsCountry.country !== HttpProvider.authCountry) {
        HttpProvider.featureFlagsCountryPending = false;
        LoggerProvider.info(this, `Country features flags missing for country ${HttpProvider.authCountry} - get them from session call (previous country: ${HttpProvider.featureFlagsCountry ? HttpProvider.featureFlagsCountry.country : 'missing'})`);
        const params = { deviceId: LoggerProvider.uuid };
        HttpProvider.featureFlagCountryPendingPromise = (async () => {
          if (APP_CONFIG.env !== Env.PROD && APP_CONFIG.apis) {
            await this.setEnvSessionForDev();
          }
          return this.get(`${APP_CONFIG.remittanceApiBaseUrl}authorization/session/v2`, params, (response: IApiAuthResponseEntity) => {
            const signinAuthToken = AuthMapper.fromTokenEntity(response.authToken, response.featureFlags);
            this.setAuthTokenFeatureFlags(signinAuthToken, null, 'after init authorization country', true);
            return response;
          });
        })();
      }
    }
  }

  setAuthTokenFeatureFlags(signinAuthToken, parsedAuthToken, reason, forceCountry = false) {
    let featureFlags = null;
    if (signinAuthToken && signinAuthToken.featureFlags)
      featureFlags = signinAuthToken.featureFlags;
    if (!featureFlags && parsedAuthToken && parsedAuthToken.FeatureFlag && parsedAuthToken.FeatureFlag.FeaturesFlag)
      featureFlags = parsedAuthToken.FeatureFlag.FeaturesFlag;

    if (featureFlags) {
      if (HttpProvider.hasAuthCustomerId(null, true) && !forceCountry) {
        HttpProvider.featureFlagsUser = featureFlags;
        this.updateFeatureFlags(reason, 'signin_token');
      } else {
        HttpProvider.featureFlagsCountry = featureFlags;
        HttpProvider.featureFlagsCountry.country = HttpProvider.authCountry;
        this.updateFeatureFlags(reason, 'session_v2');
      }
    }
  }

  static hasAuthIpIsoCode() {
    if (HttpProvider.authToken) {
      try {
        const token = jwt_decode(HttpProvider.authToken);
        if (token.ipIsoCode)
          return true;
      }
      catch (err) {
        LoggerProvider.exception(this, err, LoggerEvent.Session, 'Can\'t extract IP iso code from invalid token', err);
      }
    }
    return false;
  }

  static hasAuthCustomerId(authToken = null, fallbackReserved = false) {
    if (!authToken)
      authToken = HttpProvider.authToken;
    if (authToken) {
      try {
        const token = jwt_decode(authToken);
        if (token.customerId)
          return true;
        else if (fallbackReserved && token.ReservedCustomerId)
          return true;
      }
      catch (err) {
        LoggerProvider.exception(this, err, LoggerEvent.Session, 'Can\'t extract customer ID from invalid token', err);
      }
    }
    return false;
  }

  static getAuthCustomerId(authToken = null, fallbackReserved = false) {
    if (!authToken)
      authToken = HttpProvider.authToken;
    if (authToken) {
      try {
        const token = jwt_decode(authToken);
        if (token.customerId)
          return token.customerId;
        else if (fallbackReserved && token.ReservedCustomerId)
          return token.ReservedCustomerId;
      }
      catch (err) {
        LoggerProvider.exception(this, err, LoggerEvent.Session, 'Can\'t extract customer ID from invalid token', err);
      }
    }
    return null;
  }

  isAuthTokenExpired(authToken = null, secondsOffset = this.secondsOffset) {
    if (!authToken)
      authToken = HttpProvider.authToken;

    try {
      const token = jwt_decode(authToken);
      if ((Math.floor(Date.now()/1000) + secondsOffset) >= token.exp) {
        LoggerProvider.info(this, 'Authorization token has expired');
        return true;
      }
    }
    catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.Session, 'Can\'t extract expiry from invalid token', err);
    }
    return false;
  }

  getAuthDetails() {
    if (HttpProvider.authToken) {
      return {
        email: this.getAuthEmail(),
        userId: this.getAuthUserId()
      }
    }
    return null;
  }

  getAuthUserId() {
    if (HttpProvider.parsedApiToken)
      return HttpProvider.parsedApiToken.UserId;
    return null;
  }

  getAuthEmail() {
    if (HttpProvider.parsedAuthToken && HttpProvider.parsedAuthToken.rmtEmail && HttpProvider.parsedAuthToken.rmtEmail.indexOf('@') >= 0) // sometimes the IP is filled in it instead of the email
      return HttpProvider.parsedAuthToken.rmtEmail;
    return null;
  }

  getExternalAuthProvider() {
    if (HttpProvider.parsedAuthToken && HttpProvider.parsedAuthToken.ExternalAuthProviderName)
      return HttpProvider.parsedAuthToken.ExternalAuthProviderName;
    return null;
  }

  getAuthCustomerId() {
    if (HttpProvider.parsedAuthToken)
      return HttpProvider.parsedAuthToken.customerId;
    return null;
  }

  getAuthIsoCode() {
    if (HttpProvider.parsedAuthToken)
      return HttpProvider.parsedAuthToken.isoCode;
    return null;
  }

  getAuthCultureCode() {
    if (HttpProvider.parsedAuthToken)
      return HttpProvider.parsedAuthToken.cultureCode;
    return null;
  }

  getAuthLiteReservedCustomerId() {
    if (HttpProvider.parsedAuthToken)
      return HttpProvider.parsedAuthToken.ReservedCustomerId;
    return null;
  }

  getAuthIsLiteUser() {
    if (HttpProvider.parsedAuthToken && HttpProvider.parsedAuthToken.IsLiteUser)
      return (HttpProvider.parsedAuthToken.IsLiteUser.toLowerCase() === 'true');
    return false;
  }

  getAuthIsWalletEnabled() {
    if (HttpProvider.parsedAuthToken && HttpProvider.parsedAuthToken.IsWalletEnabled)
      return (HttpProvider.parsedAuthToken.IsWalletEnabled.toLowerCase() === 'true');
    return false;
  }

  resetAuthToken() {
    this.setAuthToken(null);
    this.initPromise = new Promise((resolve) => {
      this.requestRemittanceAuthToken(resolve);
    });
    const renewal = this.initPromise;
    HttpProvider.publicTokenRenewalInProgress = renewal;
    renewal.then(() => {
      if (HttpProvider.publicTokenRenewalInProgress === renewal)
        HttpProvider.publicTokenRenewalInProgress = null;
    });
  }

  private awaitPublicTokenRenewal(renewal: Promise<any>) {
    let timeoutId;
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        LoggerProvider.warning(this, LoggerEvent.Session, 'Public session renewal still pending - sending CST call without waiting');
        resolve(null);
      }, ConfigProvider.settings.connectionTimeout * 1000);
    });
    return Promise.race([renewal, timeout]).then(() => clearTimeout(timeoutId));
  }

  async getAuthCountry() {
    await HttpProvider.authCountryPromise;
    return HttpProvider.authCountry;
  }

  async updateAuthCountry(country) {
    let availableRegions: any = APP_CONFIG.regions;
    if (this.getAuthEmail()) // when logged in allow all regions
      availableRegions = regions;

    for (const regionName in availableRegions) {
      const region = availableRegions[regionName];
      if (region.defaultCountryFrom === country || region.regionId.toString() === country.toString()) {
        APP_CONFIG.region = { ...region };
        // only CA is using oldRemittanceLogin, we changed it to false for ARP users only [OM-16831]
        // always reset to the region's default value first to avoid stale overrides (e.g. after logout)
        try {
          if (region.region === Region.CA) {
            const agentReferralToken = await this.storageProvider.get(StorageType.AgentReferralToken);
            const agentReferralData = await this.storageProvider.getJson(StorageType.AgentReferralData);
            if (agentReferralToken || agentReferralData) {
              APP_CONFIG.region.oldRemittanceLogin = false;
            }
          }
        }
        catch (ex) {
          // ignore override errors
        }
        if (HttpProvider.authCountry !== region.defaultCountryFrom) {
          HttpProvider.authCountry = region.defaultCountryFrom;

          // Regions that enforce their own date format and ignore device locale.
          const skipLocaleUpdate = [Region.SN, Region.SG];
          if (!skipLocaleUpdate.includes(APP_CONFIG.region.region)) {
            this.updateDateLocale();
          }

          // TEMPORARY - can be removed later when feature flags come again from login/refresh or when country gets changed
          this.checkFeatureFlagsCountryPending();
          HttpProvider.featureFlagsCountryPending = false;
        }
        return true;
      }
    }
    return false;
  }

  private resolveNativeRequest(promise, parseFunction, log, url, isBridgeRequest = false): Promise<any> {
    if (this.isConnectionCheckable(url))
      this.loadingProvider.keepAlive();
    const start = Date.now();
    return new Promise((resolve, reject) => {
      promise.then(response => {
        // allow empty responses i.e. PUT /bank-deposit
        if (response.data && !(response.headers && response.headers['content-type'] && response.headers['content-type'].indexOf('text/html') >= 0)) {
          try {
            response.data = JSON.parse(response.data);
          }
          catch (ex) {
            let parsed = 'Unable to parse JSON response';
            try {
              parsed = JSON.stringify(response.data, null, 2);
            }
            catch (ex) {
            }
            LoggerProvider.error(log, LoggerEvent.CommunicationError, 'Invalid response', parsed);
            reject(response.data);
            return;
          }
        }

        const passed = Date.now() - start;
        if (log) {
          const logresponse = this.maskLog(response);
          LoggerProvider.attachDebugHeaders(LoggerProvider.log(log, log.level, LoggerEvent.CommunicationReceive, 'Received successfully (' + passed + ' ms)', logresponse), response && response.headers);
        }
        this.handleNativeResponse(response, resolve, reject, parseFunction, log);
      })
        .catch((err) => {
          const passed = Date.now() - start;
          if (!this.isConnectionCheckable(url, err.status))
            LoggerProvider.attachDebugHeaders(LoggerProvider.log(log, LoggerLevel.Info, LoggerEvent.CommunicationReceive, 'Ignore received error (' + passed + ' ms) from ' + url, err), err && err.headers);
          else
            LoggerProvider.attachDebugHeaders(LoggerProvider.error(log, LoggerEvent.CommunicationReceive, 'Received error (' + passed + ' ms) from ' + url, err), err && err.headers);
          if(err.status == 301 && !this.pendingUpdate){
            this.captureRequiredMinVersion(err.headers);
            this.pendingUpdate = true;
            this.forceUpdate();
          }else{
            this.handleNativeError(err, reject, log, url, isBridgeRequest);
          }

        });
    });
  }

  simulateErrors = false; // TODO REMOVE !!!!!!!!!!!!!!!!!!!!!!!!!!!
  private resolveRequest(observable, parseFunction, log, url, isBridgeRequest = false): Promise<any> {
    if (this.isConnectionCheckable(url))
      this.loadingProvider.keepAlive();
    const start = Date.now();
    return new Promise((resolve, reject) => {
      observable.subscribe((response) => {
        if (this.simulateErrors) {
          this.handleError({ status: 503 }, reject, log, url);
          return;
        }
        const passed = Date.now() - start;
        if (log) {
          const logresponse = this.maskLog(response);
          LoggerProvider.attachDebugHeaders(LoggerProvider.log(log, log.level, LoggerEvent.CommunicationReceive, 'Received successfully (' + passed + ' ms)', logresponse), response && response.headers);
        }
        this.handleResponse(response, resolve, reject, parseFunction, log);
      },
        (err) => {
          const passed = Date.now() - start;
          if (!this.isConnectionCheckable(url, err.status))
            LoggerProvider.attachDebugHeaders(LoggerProvider.log(log, LoggerLevel.Info, LoggerEvent.CommunicationReceive, 'Ignore received error (' + passed + ' ms) from ' + url, err), err && err.headers);
          else
            LoggerProvider.attachDebugHeaders(LoggerProvider.error(log, LoggerEvent.CommunicationReceive, 'Received error (' + passed + ' ms)', err), err && err.headers);
          this.handleError(err, reject, log, url, isBridgeRequest);
        });
    });
  }

  private isRemittanceUrl(url) {
    return url && url.indexOf(APP_CONFIG.remittanceApiBaseUrl) >= 0;
  }

  private isLogUrl(url) {
    return url && url.indexOf(APP_CONFIG.logsApiUrl) >= 0;
  }

  private isCstUrl(url) {
    if (url && url.indexOf(APP_CONFIG.API_URL) >= 0)
      return true;
    return false;
  }

  reinitialiseAuthCountry() {
    return new Promise<void>(async(resolve) => {
      await this.storageProvider.remove(StorageType.UserCountry); // really needed ?
      if (HttpProvider.sessionCountry) {
        LoggerProvider.info(this, `Has session country ${HttpProvider.sessionCountry}`);
        if (HttpProvider.sessionCountry !== HttpProvider.authCountry) {
          LoggerProvider.info(this, `Init session country ${HttpProvider.sessionCountry} (previous: ${HttpProvider.authCountry})`);
          await this.initAuthCountry(false, HttpProvider.sessionCountry);
        }
      } else {
        LoggerProvider.info(this, 'Check session country');
        await this.initAuthCountry(true);
      }
      resolve();
    });
  }

  async initAuthCountry(force:boolean = false, country = '') {
    if (!country)
      country = await this.storageProvider.get(StorageType.UserCountry);

    // Automation hook (non-PROD): replaces only the geo-IP lookup below; a stored user country still wins on a normal start.
    const automationCountry = (!country || force === true) ? readAutomationCountryOverride() : null;
    if (automationCountry) {
      country = automationCountry;
      HttpProvider.sessionCountry = country; // so reinitialiseAuthCountry() keeps it after logout
      LoggerProvider.info(this, `Using automation country override ${country}`);
    }
    else if (!country || force === true) {
      try {
        await this.getExtern(`${APP_CONFIG.remittanceApiBaseUrl}authorization/session/v2`, {}, {
          'Brand': APP_CONFIG.branding.httpBrand // important since that can have an effect on the available countries
        }, (response) => {
          const token = AuthMapper.fromTokenEntity(response.authToken, response.featureFlags);
          const decoded = jwt_decode(token.jwt);
          country = this.getSessionCountryFromToken(decoded);
          HttpProvider.sessionCountry = country;
          LoggerProvider.info(this, 'Got init country from session ' + country);
          return response;
        });
      }
      catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get user country');
      }
    }
    if (!country)
      country = APP_CONFIG.region.defaultCountryFrom;


    if (!HttpProvider.authCountry || force === true) {
      this.events.publish("displayGenericHome", true);
      LoggerProvider.info(this, 'Update init country ' + country);
      if (!(await this.updateAuthCountry(country))) {
        LoggerProvider.info(this, 'Unknown init country ' + country);
        let locale = null;
        if (this.platform.is('cordova')) {
          locale = await this.globalization.getPreferredLanguage();
          if (!locale || !locale.value)
            locale = await this.globalization.getLocaleName();
        }
        if (locale && locale.value) {
          LoggerProvider.info(this, 'Trying to fallback init country to locale ' + locale.value);
          locale = locale.value.substr(0,2).toLowerCase();
          for (let i=0; i < APP_CONFIG.regions.length; i++) {
            const region = APP_CONFIG.regions[i];
            if (region.defaultLanguageCulture && region.defaultLanguageCulture.toLowerCase().indexOf(locale) === 0) {
              LoggerProvider.info(this, 'Fallback init country to ' + region.defaultCountryFrom + ' for locale ' + locale);
              await this.updateAuthCountry(region.defaultCountryFrom);
              break;
            }
          }
        }
      }
      HttpProvider.authCountry = APP_CONFIG.region.defaultCountryFrom; // update to actually used region
    }
    else this.events.publish("displayGenericHome", false);
    HttpProvider.authCountryResolve();
  }

  private ensureRemittanceAuth() {
    if (HttpProvider.authToken) {
      if (this.isAuthTokenExpired()) {
        if (HttpProvider.hasAuthCustomerId()) {
          LoggerProvider.info(this, 'Ensure token: token has been expired already');
          this.routeToStart();
        }
        this.resetAuthToken();
        return this.initPromise;
      }
    }
    this.initRemittanceAuthToken();
    if (this.initPromise)
      return this.initPromise;
    return null;
  }

  initRemittanceAuthToken() {
    if (HttpProvider.authToken)
      return null;
    else if (this.initPromise)
      return this.initPromise;

    const promise = new Promise(async(resolve) => {
      try {
        const auth = await this.storageProvider.get(StorageType.AuthToken);
        const refresh = await this.storageProvider.get(StorageType.RefreshToken);
        if (APP_CONFIG.env !== Env.PROD && APP_CONFIG.apis) {
          await this.setEnvSessionForDev();
        }
        if (auth && auth !== 'null' && HttpProvider.hasAuthCustomerId(auth)) {
          // check if stored session token is from a login, otherwise there is no reason to reuse it
          let refreshResult = null;
          if (!this.isAuthTokenExpired(auth) && refresh) {
            LoggerProvider.info(this, 'Seems to have valid session - try to resume');
            const token = {
              jwt: auth,
              refresh: await this.storageProvider.get(StorageType.RefreshToken)
            };
            this.setAuthToken(token);
            refreshResult = await this.refresh(false, false, RefreshUserDetails.Full);
          }
          if (refreshResult) {
            LoggerProvider.info(this, 'Session successfully resumed');
            resolve({ refreshResult });
          } else {
            LoggerProvider.info(this, 'Session expired - creating new session');
            this.setAuthToken(null);
            this.requestRemittanceAuthToken(resolve);
          }
        }
        else {
          this.requestRemittanceAuthToken(resolve);
        }
      } catch(ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Exception during initialize remittance auth token');
        this.requestRemittanceAuthToken(resolve);
      }
    });
    this.initPromise = promise;
    return this.initPromise;
  }

  async setEnvSessionForDev() {
    const env = await this.storageProvider.get(StorageType.SessionEnv);
    LoggerProvider.info(this, `Session had preselected environment ${env}`);
    if (env) {
      const api = APP_CONFIG.apis.find((a) => a.value === env);
      if (api) {
        this.setSessionEnv(api, null, null);
      }
    }
  }

  public setSessionEnv(api, authProvider, utilsProvider) {
    APP_CONFIG.selectedApi = api;
    APP_CONFIG.API_URL = api.cst;
    APP_CONFIG.remittanceApiBaseUrl = api.remittance;
    APP_CONFIG.logsApiUrl = api.logApi;
    this.storageProvider.set(StorageType.SessionEnv, api.value);
    if (authProvider)
      authProvider.resetUserCountryFields(false, true);
    if (utilsProvider)
      utilsProvider.refreshCountryFeatures();
  }

  private async requestRemittanceAuthToken(resolve) {

    await HttpProvider.authCountryPromise;

    this.deviceProvider.getDeviceInfo().then(deviceInfo => {
      const params = { ...deviceInfo, deviceId: LoggerProvider.uuid };
      const headers = {
        'X-Forwarded-For': APP_CONFIG.region.regionalIp // transmit the corresponding IP for the region, so the region-lock won't complain
      };

      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Fetched device info', deviceInfo);

      const currentAuthToken = HttpProvider.authToken;
      this.get(`${APP_CONFIG.remittanceApiBaseUrl}authorization/session/v2`, params, (response: IApiAuthResponseEntity) => {
        HttpProvider.sessionResponse = response;
        if (currentAuthToken !== HttpProvider.authToken) // if token has been changed meanwhile (i.e. login) don't continue ! Otherwise we would risk to overwrite a login token with a public token
          return HttpProvider.authToken;
        if (HttpProvider.hasAuthCustomerId()) // if user is logged in (e.g. retry after failed session call) don't overwrite the login token with a public token
          return HttpProvider.authToken;

        const token = AuthMapper.fromTokenEntity(response.authToken, response.featureFlags);
        this.setAuthToken(token);
        if (response.phpApiBaseUrl)
          APP_CONFIG.phpApiBaseUrl = response.phpApiBaseUrl;
        if (response.configSettings) {
          ConfigProvider.lastReceived = new Date();
          ConfigProvider.settings = { ...ConfigProvider.settings, ...response.configSettings };
        }
        this.initPromise = null;
        return HttpProvider.authToken;
      }, headers)
        .then(() => { resolve(); })
        .catch((err) => {
          LoggerProvider.error(this, LoggerEvent.Session, 'Failed to initialize session', err);
          window.setTimeout(() => {
            this.requestRemittanceAuthToken(resolve);
          }, 5000);
        })
    })
      .catch(err => {
        LoggerProvider.log(this, LoggerLevel.Error, LoggerEvent.UnhandledException, 'Error fetching device info', err)
        resolve(false);
      });
  }

  public requestAuthTokenForUS(): Promise<any> {
    return new Promise<boolean | void>(async (resolve, reject) => {
      try {
        const deviceInfo = await this.deviceProvider.getDeviceInfo();

        const params = { ...deviceInfo };
        const headers = { 'X-Forwarded-For': APP_CONFIG.region.regionalIp,  'IsoCode': 'US' };

        const currentAuthToken = HttpProvider.authToken;
        const resolveFunc = (response: IApiAuthResponseEntity) => {
          if (currentAuthToken !== HttpProvider.authToken) // if token has been changed meanwhile (i.e. login) don't continue ! Otherwise we would risk to overwrite a login token with a public token
            return HttpProvider.authToken;
          if (HttpProvider.hasAuthCustomerId()) // if user is logged in (e.g. retry after failed session call) don't overwrite the login token with a public token
            return HttpProvider.authToken;

          const token = AuthMapper.fromTokenEntity(response.authToken, response.featureFlags);
          this.setAuthToken(token);

          if (response.phpApiBaseUrl) APP_CONFIG.phpApiBaseUrl = response.phpApiBaseUrl;
          if (response.configSettings) {
            ConfigProvider.lastReceived = new Date();
            ConfigProvider.settings = { ...ConfigProvider.settings, ...response.configSettings };
          }
          this.initPromise = null;
          return HttpProvider.authToken;
        }

        this.get(APP_CONFIG.remittanceApiBaseUrl + 'authorization/session/v2', params, resolveFunc, headers)
        .then(() => resolve(true))
        .catch(err => {
          LoggerProvider.error(this, LoggerEvent.Session, 'Failed to initialize session', err);
          window.setTimeout(() => this.requestAuthTokenForUS(), 5000);
        });
      } catch(ex) {
        LoggerProvider.log(this, LoggerLevel.Error, LoggerEvent.UnhandledException, 'Error fetching device info', ex)
        resolve(false);
      }
    });
  }

  getHeaderVersion() {
    if (!this.isNewerVersion(HttpProvider.minVersion, HttpProvider.version))
      return HttpProvider.minVersion;
    return HttpProvider.version;
  }

  private async createHeaders(url, body = null, contentType = 'application/json', isBridgeRequest = false) {
    // check if there is a pending resume and wait first for it to be resolved
    HttpProvider.setLastActivity();
    if (HttpProvider.inactivityPromise) {
      LoggerProvider.info(this, 'Wait for resume for create headers');
      await HttpProvider.inactivityPromise;
      HttpProvider.inactivityPromise = null;
      LoggerProvider.info(this, 'Cleared inactivity promise in create headers');
    }

    const waitForReady = this.checkWaitForReady(url, isBridgeRequest);
    if (waitForReady)
      await HttpProvider.readyPromise;


    // ApiGuard headers
    // body must stay null per the F5 integration guide unless F5 instructs otherwise
    let apiGuardHeaders = {};
    try {
      apiGuardHeaders = await this.apiGuardProvider.getRequestHeaders(url, null);
    } catch(err){
      LoggerProvider.error(this, LoggerEvent.CommunicationError, 'Error getting ApiGuard headers', err);
    }

    // Pass in current language so we get translations
    const cultureCode = HttpProvider.currentLanguage;
    if (cultureCode)
      this.cultureCode = cultureCode;

    // don't check remittance auth while we are doing relogin since we know the token is expired and only allow you to reach here either
    // when the URL is allowed to progress (i.e. /log) or relogin has been finished
    if (!HttpProvider.relogging && this.isRemittanceBased(url) && !url.endsWith('/session/v2') && !url.endsWith('/refresh')) {
      if (url.endsWith('/log')) // log should not trigger a reset of the auth token and just wait if something an auth token is in progress
        await this.initPromise;
      else
        await this.ensureRemittanceAuth();
    } else if (this.isCstUrl(url) && HttpProvider.publicTokenRenewalInProgress && !HttpProvider.hasAuthCustomerId(null, true)) {
      await this.awaitPublicTokenRenewal(HttpProvider.publicTokenRenewalInProgress);
    } else if (this.isCstUrl(url) && HttpProvider.authToken && !HttpProvider.hasAuthCustomerId(null, true) && this.isAuthTokenExpired()) {
      await this.awaitPublicTokenRenewal(this.ensureRemittanceAuth());
    }

    const version = this.getHeaderVersion();
    let headers: any = {
      ...DEFAULT_HEADERS,
      ...apiGuardHeaders,
      'Content-Type': contentType,
      'Authorization': `Bearer ${HttpProvider.authToken}`,
      'AppVersion': version,
      'Brand': APP_CONFIG.branding.httpBrand,
      'CultureCode': this.cultureCode,
      'IsoCode': APP_CONFIG.region.defaultCountryFrom,
      'Locale': this.cultureCode,
      'AmplitudeSessionID': (SegmentProvider.amplitudeSessionId) ? SegmentProvider.amplitudeSessionId : ''
    };

    if (/*this.platform.is('android') &&*/ navigator && navigator.languages) {
      let langs = '';
      for (const lang of navigator.languages) {
        if (langs.length > 0)
            langs += ',';
        langs += lang;
      }
      headers = { ...headers, 'Accept-Language': langs };
    }

    if (url && !this.isRemittanceBased(url))
      headers['DeviceId'] = LoggerProvider.uuid; // TODO !!!!!!!! ADD SUPPORT TO REMITTANCE TO SUPPORT THAT HEADER

    headers['CountryId'] = APP_CONFIG.region.regionId.toString();

    // IMPROVE WORKAROUND: the countries come incomplete for some european languages and Remittance might throw errors (i.e order/status)
    // NOT NEEDED AT THE MOMENT BUT KEEP IF IT'S NEEDED
    /*
    if (this.isRemittanceUrl(url) &&
        this.cultureCode !== 'en-US' && this.cultureCode !== 'es-US' &&
        this.cultureCode !== 'en-CA' && this.cultureCode !== 'fr-CA') {
      headers['Locale'] = 'en-US';
      headers['CultureCode'] = 'en-US';
    }
    */
    if (url && !(this.isRemittanceBased(url))) // TODO !!! remove it in production and staging
      headers['AmplitudeDeviceID'] = LoggerProvider.uuid; // TODO !!! remove it in production and staging
    if (url && !(this.isLogUrl(url)) && APP_CONFIG.amplitudeHeader) // !!! TODO remove app_config check in production and staging
      headers['AmplitudeDeviceID'] = LoggerProvider.uuid;
    if (url && url.indexOf('https://qa') === -1)
      headers['SegmentAnonymousID'] = SegmentProvider.segmentAnonymousId;



    // IMPROVE WORKAROUND: CST is throwing errors for any region code except en-US and en-CA (like en-IT)
    let overrideCultureCode = null;
    if(url.endsWith('/get-bin-info')) {
        overrideCultureCode = this.setCultureCode();
    } else if (this.cultureCode && this.cultureCode.substr(0,3) === 'en-' && this.cultureCode !== 'en-US' && this.cultureCode !== 'en-CA' && this.cultureCode !== 'en-CH'
    && this.cultureCode !== 'en-GB') {
        overrideCultureCode = "en-US";
    }
    if (overrideCultureCode) {
        headers['Locale'] = overrideCultureCode;
        headers['CultureCode'] = overrideCultureCode;
    }
    if (this.platform.is('cordova')) {
      headers['Platform'] = this.device.platform;
      headers['PlatformVersion'] = this.device.version;
      /*
      if (this.platform.is('ios'))
        headers['Origin'] = 'ionic://localhost';
      else
        headers['Origin'] = 'http://localhost';
      */
    }
    else {
      headers['Platform'] = 'android';
      headers['PlatformVersion'] = version;
    }

    return headers;
  }
  private isRemittanceBased(url: any) {
    return this.isRemittanceUrl(url) || this.isLogUrl(url);
  }

  setCultureCode() {
    if(APP_CONFIG.region.defaultLanguageCulture == 'en-GB') {
      return "en-GB";
    }
    if(APP_CONFIG.region.defaultCountryFrom == 'CH') {
      const langValue = HttpProvider.currentLanguage;
      const langArray = ["en","fr","it","de"];
      for(let element of langArray) {
        if(langValue.startsWith(element)) {
          return `${element}-CH`;
        }
      }
      return "en-CH";
    }
  }

  public async postExtern(url: string, body: any, headers: any, responseType, parseFunction = null, forceSslPinning = false): Promise<any> {
    const logbody = this.maskLog(body);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'POST (extern) ' + HttpProvider.sendLogExtra + url, logbody);
    LoggerProvider.attachDebugHeaders(log, headers);
    const checked = await this.checkConnection(log, url);
    if (!checked)
      return null;

    if (this.platform.is('cordova')) {
      if (headers['Content-Type'] === 'application/x-www-form-urlencoded')
        this.httpNative.setDataSerializer('urlencoded');
      else
        this.httpNative.setDataSerializer('json');
      await this.prepareExternHttpNative(url, forceSslPinning);
      return this.resolveNativeRequest(this.httpNative.post(url, body, headers), parseFunction, log, url);
    }
    else
      return this.resolveRequest(this.http.post(url, body, { observe: 'response', headers: new HttpHeaders(headers), responseType: responseType }), parseFunction, log, url);
  }

  public async post(url: string, body: any, parseFunction = null, customHeaders: any = null, isBridgeRequest = false): Promise<any> {
    let level = LoggerLevel.Notice;
    let logbody = body;
    // log calls shouldn't log the body otherwise we send it duplicated
    if (url.endsWith('/log.php') || url.endsWith('/log')) {
      logbody = null;
      level = LoggerLevel.Debug;
    }
    else {
      // For certain API calls, we log to logrocket console after sanitizing all user identifiable info
      if (this.logRocketProvider.tologApiCall(url) && logbody) {
        var duplicateLogBody = this.maskLog(logbody, true);
        this.logRocketProvider.toLogRocketConsole(level, LoggerEvent.CommunicationSend, url, duplicateLogBody);
      }
      logbody = this.maskLog(logbody);
    }

    const log = LoggerProvider.log(this, level, LoggerEvent.CommunicationSend, 'POST ' + HttpProvider.sendLogExtra + url, logbody);
    const checked = await this.checkConnection(log, url, isBridgeRequest);
    if (!checked)
      return null;

    let headers = await this.createHeaders(url, body, 'application/json', isBridgeRequest);


    if (customHeaders)
      headers = { ...headers, ...customHeaders};

    headers = this.cleanUpHeaders(headers);
    LoggerProvider.attachDebugHeaders(log, headers);

    if (this.platform.is('cordova')) {
      this.prepareHttpNative(url); //timeout 30 seconds
      if (headers['Content-Type'] === 'application/x-www-form-urlencoded')
        this.httpNative.setDataSerializer('urlencoded');
      else
        this.httpNative.setDataSerializer('json');
      return this.resolveNativeRequest(this.httpNative.post(url, body, headers), parseFunction, log, url, isBridgeRequest);
    }
    else
      return this.resolveRequest(this.http.post(url, body, { observe: 'response', headers: new HttpHeaders(headers) }), parseFunction, log, url, isBridgeRequest);
  }

  public async putExtern(url: string, body: any, headers: any, responseType, parseFunction = null): Promise<any> {
    const logbody = this.maskLog(body);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'PUT (extern) ' + HttpProvider.sendLogExtra + url, logbody);
    LoggerProvider.attachDebugHeaders(log, headers);
    const checked = await this.checkConnection(log, url);
    if (!checked)
      return null;

    if (this.platform.is('cordova')) {
      if (headers['Content-Type'] === 'application/x-www-form-urlencoded')
        this.httpNative.setDataSerializer('urlencoded');
      else
        this.httpNative.setDataSerializer('json');
      await this.prepareExternHttpNative(url);
      return this.resolveNativeRequest(this.httpNative.put(url, body, headers), parseFunction, log, url);
    }
    else
      return this.resolveRequest(this.http.put(url, body, { observe: 'response', headers: new HttpHeaders(headers), responseType: responseType }), parseFunction, log, url);
  }

  public async put(url: string, body: any, parseFunction = null, customHeaders: any = null, isBridgeRequest = false): Promise<any> {
    const logbody = this.maskLog(body);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'PUT ' + HttpProvider.sendLogExtra + url, logbody);
    const checked = await this.checkConnection(log, url, isBridgeRequest);
    if (!checked)
      return null;

    let headers = await this.createHeaders(url, body, 'application/json', isBridgeRequest);

    if (customHeaders)
      headers = { ...headers, ...customHeaders };

    headers = this.cleanUpHeaders(headers);
    LoggerProvider.attachDebugHeaders(log, headers);

    if (this.platform.is('cordova')) {
      this.prepareHttpNative();
      return this.resolveNativeRequest(this.httpNative.put(url, body, headers), parseFunction, log, url, isBridgeRequest);
    }
    else
      return this.resolveRequest(this.http.put(url, body, { observe: 'response', headers: new HttpHeaders(headers) }), parseFunction, log, url, isBridgeRequest);
  }

  public async patch(url: string, body: any, parseFunction = null, customHeaders: any = null, isBridgeRequest = false): Promise<any> {
    const logbody = this.maskLog(body);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'PATCH ' + HttpProvider.sendLogExtra + url, logbody);
    const checked = await this.checkConnection(log, url, isBridgeRequest);
    if (!checked)
      return null;

    let headers = await this.createHeaders(url, body, 'application/json', isBridgeRequest);

    if (customHeaders)
      headers = { ...headers, ...customHeaders };

    headers = this.cleanUpHeaders(headers);

    if (this.platform.is('cordova')) {
      this.prepareHttpNative();
      return this.resolveNativeRequest(this.httpNative.patch(url, body, headers), parseFunction, log, url, isBridgeRequest);
    }
    else
      return this.resolveRequest(this.http.patch(url, body, { observe: 'response', headers: new HttpHeaders(headers) }), parseFunction, log, url, isBridgeRequest);
  }

  public async getExtern(url: string, params = {}, headers: any = {}, parseFunction = null, forceBrowser = false): Promise<any> {
    const logparams = this.maskLog(params);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'GET (extern) ' + HttpProvider.sendLogExtra + url, logparams);
    LoggerProvider.attachDebugHeaders(log, headers);
    const checked = await this.checkConnection(log, url);
    if (!checked)
      return null;

    if (this.platform.is('cordova') && !forceBrowser) {
      await this.prepareExternHttpNative(url);
      return this.resolveNativeRequest(this.httpNative.get(url, this.prepareNativeParams(params), headers), parseFunction, log, url);
    }
    else
      return this.resolveRequest(this.http.get(url, { observe: 'response', headers: new HttpHeaders(headers), params: ProviderUtilities.objectToHttpParams(params) }), parseFunction, log, url);
  }

  public async get(url: string, params = {}, parseFunction = null, customHeaders: any = null, isBridgeRequest = false): Promise<any> {
    const logparams = this.maskLog(params);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'GET ' + HttpProvider.sendLogExtra + url, logparams);
    const checked = await this.checkConnection(log, url, isBridgeRequest);
    if (!checked)
      return null;

    let headers = await this.createHeaders(url, null, 'application/json', isBridgeRequest);

    if (customHeaders)
      headers = { ...headers, ...customHeaders };

    headers = this.cleanUpHeaders(headers);
    LoggerProvider.attachDebugHeaders(log, headers);

    if (this.platform.is('cordova')) {
      this.prepareHttpNative();
      return this.resolveNativeRequest(this.httpNative.get(url, this.prepareNativeParams(params), headers), parseFunction, log, url, isBridgeRequest);
    }
    else
      return this.resolveRequest(this.http.get(url, { observe: 'response', headers: new HttpHeaders(headers), params: ProviderUtilities.objectToHttpParams(params) }), parseFunction, log, url, isBridgeRequest);
  }

  public async deleteExtern(url: string, params = {}, headers: any = {}, parseFunction = null): Promise<any> {
    const logparams = this.maskLog(params);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'GET (extern) ' + HttpProvider.sendLogExtra + url, logparams);
    LoggerProvider.attachDebugHeaders(log, headers);
    const checked = await this.checkConnection(log, url);
    if (!checked)
      return null;

    if (this.platform.is('cordova')) {
      await this.prepareExternHttpNative(url);
      return this.resolveNativeRequest(this.httpNative.delete(url, this.prepareNativeParams(params), headers), parseFunction, log, url);
    }
    else
      return this.resolveRequest(this.http.delete(url, { observe: 'response', headers: new HttpHeaders(headers), params: ProviderUtilities.objectToHttpParams(params) }), parseFunction, log, url);
  }

  public async delete(url: string, params = {}, parseFunction = null, customHeaders: any = null, isBridgeRequest = false): Promise<any> {
    const logparams = this.maskLog(params);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'DELETE ' + HttpProvider.sendLogExtra + url, logparams);
    const checked = await this.checkConnection(log, url, isBridgeRequest);
    if (!checked)
      return null;

    let headers = await this.createHeaders(url, null, 'application/json', isBridgeRequest);

    if (customHeaders)
      headers = { ...headers, ...customHeaders };

    headers = this.cleanUpHeaders(headers);
    LoggerProvider.attachDebugHeaders(log, headers);

    if (this.platform.is('cordova')) {
      this.prepareHttpNative();
      return this.resolveNativeRequest(this.httpNative.delete(url, this.prepareNativeParams(params), headers), parseFunction, log, url, isBridgeRequest);
    }
    else
      return this.resolveRequest(this.http.delete(url, { observe: 'response', headers: new HttpHeaders(headers), params: ProviderUtilities.objectToHttpParams(params) }), parseFunction, log, url, isBridgeRequest);
  }

  public async downloadFile(url: string, params = {}, filename: string, contentType: string = 'application/pdf') {
    const logparams = this.maskLog(params);
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'DOWNLOAD ' + HttpProvider.sendLogExtra + url, logparams);
    const checked = await this.checkConnection(log, url);
    if (!checked)
      return null;

    let headers = await this.createHeaders(url, null, contentType);
    headers = this.cleanUpHeaders(headers);
    LoggerProvider.attachDebugHeaders(log, headers);

    if (this.platform.is('cordova')) {
      this.prepareHttpNative();
      const start = Date.now();
      return new Promise((resolve, reject) => {
        this.httpNative.downloadFile(url, this.prepareNativeParams(params), headers, filename)
          .then((response) => {
            const passed = Date.now() - start;
            LoggerProvider.log(log, log.level, LoggerEvent.CommunicationReceive, 'Download successfully (' + passed + ' ms)', response);
            resolve(response);
          })
          .catch((err) => {
            const passed = Date.now() - start;
            LoggerProvider.error(log, LoggerEvent.CommunicationReceive, 'Download error (' + passed + ' ms)', err);
            reject(err);
          });
      });
    }
    else
      return new Promise((resolve, reject) => {
        reject('Not supported in browser');
        return resolve;
      });
  }

  /**
   * POST a JSON body and receive the raw response bytes, for endpoints that generate a
   * document on demand. Goes straight to the cordova plugin because the @ionic-native
   * wrapper has no responseType support. Native only, like downloadFile; errors go through
   * handleNativeError as a bridge request so an expired session is handled, not just reported.
   */
  public async postBinary(url: string, body: any, accept: string = 'application/pdf'): Promise<ArrayBuffer> {
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'DOWNLOAD (POST) ' + HttpProvider.sendLogExtra + url, this.maskLog(body));
    const checked = await this.checkConnection(log, url);
    if (!checked)
      return null;

    let headers = await this.createHeaders(url, body);
    headers = this.cleanUpHeaders({ ...headers, Accept: accept });

    if (!this.platform.is('cordova'))
      throw new Error('Not supported in browser');

    this.prepareHttpNative();
    const start = Date.now();
    return new Promise<ArrayBuffer>((resolve, reject) => {
      (window as any).cordova.plugin.http.sendRequest(url, {
        method: 'post',
        data: body,
        serializer: 'json',
        headers,
        responseType: 'arraybuffer',
      }, (response) => {
        const passed = Date.now() - start;
        LoggerProvider.log(log, log.level, LoggerEvent.CommunicationReceive, 'Download successfully (' + passed + ' ms)', { status: response.status });
        resolve(response.data);
      }, (err) => {
        const passed = Date.now() - start;
        LoggerProvider.error(log, LoggerEvent.CommunicationReceive, 'Download error (' + passed + ' ms)', err);
        this.handleNativeError(err, reject, log, url, true);
      });
    });
  }

  public multipleCalls(calls: Promise<any>[], parseFunction = null, errorFunction = null, isReject = false): Promise<any> {
    const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'Multiple calls');

    return new Promise((resolve, reject) => {
      Promise.all(calls).then((data) => {
        LoggerProvider.log(log, LoggerLevel.Notice, LoggerEvent.CommunicationReceive, 'Received multiple calls');
        if (parseFunction)
          data = parseFunction(data);
        resolve(data);
      })
        .catch((err) => {
          LoggerProvider.exception(log, err, LoggerEvent.CommunicationError, 'Multiple calls failed');
          if (errorFunction) {
            errorFunction(err);
          }
          if (isReject)
            reject(err)
          resolve(null);
        });
    });
  }

  prepareNativeParams(params) {
    // advanced-http: params needs to be an object with strings
    const converted = {};
    for (const p in params) {
      const param = params[p];
      if (param)
        converted[p] = param.toString();
    }
    return converted;
  }

  async getAsync(promise) {
    try {
      const result = await promise
      return { result: result };
    }
    catch (ex) {
      return { exception: ex };
    }
  }

  async finishAsync(promise) {
    const result = await promise;
    if (result) {
      if (result.exception)
        throw result.exception;
      return result.result;
    }
    return null;
  }

  maskLog(body, removeIdentifiableInfo: boolean = false) {
    if (!body)
      return 'Empty';
    return this.routeProvider.maskLog(body, removeIdentifiableInfo);
  }

  private populateRiaSession(response: any) {
    // Set the riaSession object with data contained in the Session-Analytics header
    try {
      const riaSessionJson = response.headers['session-analytics'];
      if (riaSessionJson) {
        const riaSession = JSON.parse(riaSessionJson);
        window['riaSession'] = riaSession;
      }
    }
    catch (err) {
      LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.Info, 'Error Processing RiaSession' + (err || {}).toString(), err);
    }
  }

  private populateWebRiaSession(response: any) {
    // Set the riaSession object with data contained in the Session-Analytics header
    try {
      const riaSessionJson = response.headers.get('session-analytics');
      if (riaSessionJson) {
        const riaSession = JSON.parse(riaSessionJson);
        window['riaSession'] = riaSession;
      }
    }
    catch (err) {
      LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.Info, 'Error Processing RiaSession' + (err || {}).toString(), err);
    }
  }

  public getHeadersValue(headers: any, headerName: string) {
    return this.platform.is('cordova') ? headers[headerName] : headers.get(headerName);
  }

  private captureRequiredMinVersion(headers: any) {
    if (!headers) return;
    let value: string;
    try {
      value = typeof headers.get === 'function' ? headers.get('version-deprecated') : headers['version-deprecated'];
    } catch (ex) {
      return;
    }
    if (value)
      HttpProvider.requiredMinVersion = value;
  }

  public async getAppVersion() {
    return this.http.get(this.versionFileURL).toPromise();
  }

  forceUpdate(){
    this.analytics.trackEvent({
      eventName: AnalyticsEventType.ForceUpdateRequested,
      analyticsData: { requiredMinVersion: HttpProvider.requiredMinVersion }
    });
    const title = this.translateService.instant('page.preLoginPage.mandatoryAppUpdateHeader');
    const subTitle = this.translateService.instant('page.preLoginPage.mandatoryAppUpdateMessage');
    const buttons = [
      {
        text: this.translateService.instant('page.preLoginPage.updateButton'),
        handler: () => {
          this.analytics.trackEvent({
            eventName: AnalyticsEventType.ForceUpdateCTAClicked,
            analyticsData: { requiredMinVersion: HttpProvider.requiredMinVersion }
          });
          let url;
          if (this.platform.is('android')) {
            url = APP_CONFIG.market.androidUrl;
            this.iab.create(url, '_system');
          } else if (this.platform.is('ios')) {
            url = APP_CONFIG.market.iosUrl;
            window.location.href = url;
          }
          this.pendingUpdate = false;
          return false; // Prevent the dismiss of the alert
        }
      }
    ];
    const alert = this.alertCtrl.create({cssClass: 'popup-error', title,subTitle, buttons, enableBackdropDismiss: false});
    alert.setMode('ios');
    alert.present();
  }

  cleanUpHeaders(headers) {
    const deletes = [];
    for (const key in headers) {
      if (headers[key] === null || headers[key] === undefined)
        deletes.push(key);
      else if (typeof headers[key] !== 'string')
        // Native transport (cordova-plugin-advanced-http) throws on non-string header values
        // (browser HttpClient coerces silently); coerce so native matches browser behaviour.
        headers[key] = String(headers[key]);
    }
    for (const key of deletes) {
      delete headers[key];
    }
    return headers;
  }

  public async refresh(showLoader: boolean = false, redirect: boolean = true, userDetails = RefreshUserDetails.None) {
    if (HttpProvider.refreshInProgress) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Skip refresh since it is already in progress');
      return;
    }
    else
      LoggerProvider.info(this, 'Refresh token');

    if (showLoader)
      this.loadingProvider.show();

    this.lastRefresh = new Date();
    clearTimeout(this.refreshTimeout);
    let refreshResolve: any = null;
    HttpProvider.refreshInProgress = new Promise((resolve) => {
      refreshResolve = resolve;
    });
    let result = null;
    let time = 10000;
    try {
      let url = `${APP_CONFIG.API_URL}api/auth/v3/refresh`;
      if (APP_CONFIG.featureFlags.signinOld)
          url = `${APP_CONFIG.API_URL}api/auth/refresh`;

      let refreshToken = HttpProvider.refreshToken;
      if (!refreshToken)
        refreshToken = await this.storageProvider.get(StorageType.RefreshToken);
      let deviceToken = HttpProvider.deviceToken;
      if (!deviceToken)
        deviceToken = await this.storageProvider.get(StorageType.DeviceToken);
      let request: any = {
        RefreshToken: refreshToken,
        DeviceToken: deviceToken,
        RefreshUserDetails: userDetails
      };

      result = await this.post(url, request, (response: any) => {
        LoggerProvider.info(this, 'Received refresh response');

        if (response && response.Value) {
          // TODO CHECK USER !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

          LoggerProvider.info(this, 'Got refresh token');
          const signin: any = AuthMapper.fromSigninEntity(response.Value);
          if (signin.authToken) {
            signin.Value = response.Value;
            this.setAuthToken(signin.authToken);
            time = HttpProvider.authExpiresInSeconds * 1000 - ConfigProvider.settings.refreshGraceSeconds;
            LoggerProvider.info(this, 'Set refresh token');

            // if we received new user information update it
            if (response.Value.User && userDetails == RefreshUserDetails.Full) {
              HttpProvider.user = response.Value.User;
              // if we get feature flags update them (because of circular dependencies we can't use parseFeatureFlags so let's do it manually)
              if (HttpProvider.user.FeatureFlags) {
                HttpProvider.featureFlagsUser = HttpProvider.user.FeatureFlags;
                this.updateFeatureFlags('refresh', 'signin_token');
              }
            }

            if (showLoader)
              this.loadingProvider.hide();

            return signin;
          }
          else
            LoggerProvider.error(this, LoggerEvent.ProviderException, 'Missing Authtoken after refresh !');
        }
        else if (response && response.Result && response.Result.RetVal === 20) {
          LoggerProvider.error(this, LoggerEvent.ProviderException, 'Refresh token expired !');
          if (redirect)
            this.routeToStart();
        }
        else if (response && response.Result && response.Result.RetVal !== 1) {
          LoggerProvider.error(this, LoggerEvent.ProviderException, 'Invalid result ' + response.Result.RetVal + ' - should log out ?');
        }

        if (showLoader)
          this.loadingProvider.hide();

        return null;
      });
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to set refresh token');
    }
    HttpProvider.refreshInProgress = null;
    refreshResolve(true);

    if (result && result.authToken)
      this.events.publish(EVENTS_NAME.WALLET_BALANCE_REFRESH);

    this.startRefreshTimer();

    return result;
  }

  startRefreshTimer() {
    this.lastRefresh = new Date();
    let time = HttpProvider.authExpiresInSeconds * 1000 - ConfigProvider.settings.refreshGraceSeconds;
    if (HttpProvider.featureFlags && HttpProvider.featureFlags['SkipRefreshMaxLimit']) {
      if (!HttpProvider.skipRefreshMaxLimit)
        LoggerProvider.info(this, 'Activated skip refresh max limit');
      HttpProvider.skipRefreshMaxLimit = true;
    }

    if (time < 10000)
      time = 10000;
    else if (time > 60000 && !HttpProvider.skipRefreshMaxLimit)
      time = 60000;

    if (HttpProvider.featureFlags && HttpProvider.featureFlags['ForceRefreshTime']) {
      const refreshTime = parseInt(HttpProvider.featureFlags['ForceRefreshTime']);
      if (HttpProvider.forceRefreshTime !== refreshTime)
        LoggerProvider.info(this, `Activated force refresh time with value ${refreshTime}`);
      HttpProvider.forceRefreshTime = refreshTime;
      if (HttpProvider.forceRefreshTime > 0)
        time = HttpProvider.forceRefreshTime;
    }

    LoggerProvider.info(this, 'Set refresh timeout for ' + time + 'ms');
    this.refreshTimeout = setTimeout(async () => {
      await this.refresh();
    }, time);
  }

  clearRefreshTimer() {
    clearTimeout(this.refreshTimeout);
  }

  private trackNetworkError(error: string, url: string, status: number): void {
    const statusDescription = HttpStatusDescriptions[status] || `${status}`;

    const statusWithDescription = statusDescription === status.toString()
        ? `${status}`
        : `${status} ${statusDescription}`;

    this.analytics.trackEvent({
        eventName: AnalyticsEventType.NetworkError,
        analyticsData: {
            error,
            errorCode: statusWithDescription,
            url,
        }
    });
  }

  updateDateLocale() {
      // update date format based on devices locale
      let format = '';
      let locale = '';
      try {
        let dateFormat = '';
        locale = (new Intl.NumberFormat()).resolvedOptions().locale;
        format = new Intl.DateTimeFormat(locale).format(new Date('2015-11-12'));

        if (format.indexOf('12') == 0) {
          // Day i.e. 12.11.2015 (de-DE)
          dateFormat += 'DD/';
        } else if (format.indexOf('11') == 0) {
          // Month i.e. 11/12/2015 (en-US)
          dateFormat += 'MM/';
        } else if (format.indexOf('2015') == 0) {
          // Year i.e. 2025-11-12 (en-CA)
          dateFormat += 'YYYY/';
          format = format.substring(2); // convert from 2015 to 15 so date and month will be on the same position below
        }

        if (dateFormat.length > 0) {
          if (format.indexOf('12') == 3) {
            // Day i.e. 11/12/2015 (en-US)
            dateFormat += 'DD';
          } else if (format.indexOf('11') == 3) {
            // Month i.e. 2025-11-12 (en-CA)
            dateFormat += 'MM';
          }
        }

        if (dateFormat.length > 0) {
          if (format.indexOf('2015') == 6) {
            // Year i.e. 11/12/2015 (en-US)
            dateFormat += '/YYYY';
          } else if (format.indexOf('12') == 6) {
            // Day i.e. 2025-11-12 (en-CA)
            dateFormat += '/DD';
          } else if (format.indexOf('11') == 6) {
            // Month i.e. ? - just to be on the safe side
            dateFormat += '/MM';
          }
        }

        if (dateFormat.length === 10)
          APP_CONFIG.region.dateFormat = dateFormat;
        LoggerProvider.info(this, `Updated region date format ${locale} => ${format} => ${APP_CONFIG.region.dateFormat}`);
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, `Failed to update region date format ${locale} => ${format}`);
      }
  }

  getSessionCountryFromToken(decoded) {
    let country = decoded.ipIsoCode;
    // TODO: first only for other brandings since we only ran there into problems (i.e. testing Miranda which is only allowed in the US in DE caused problems for the endpoint NewCustomerRequiredFields)
    // later eventually can be expanded for everyone but keep it only for brands to be on the safe side for now
    if (APP_CONFIG.branding && APP_CONFIG.branding.branding !== brandings.RIA.branding) {
      if (decoded.isoCode && decoded.isoCode !== country) {
        LoggerProvider.warning(this, LoggerEvent.IpEvent, `Seems unsupported country ${country} hence switching to ${decoded.isoCode}`)
        country = decoded.isoCode;
      }
    }
    if (country === 'GB')
      country = 'UK';
    if (country === 'YT' || country === 'GP' || country === 'MQ' || country === 'RE' || country === 'MF' || country === 'GF') {
      LoggerProvider.info(this, `French territory country ${country}`)
      country = 'FR';
    }
    return country;
  }

  public isTestVersion() {
    if (APP_CONFIG.env === Env.PROD && (HttpProvider.minVersion === HttpProvider.betaVersion || HttpProvider.minVersion === '5.0.1'))
      return true;
    return false;
  }

  async getNewHeader(url: string){
    let headers = await this.createHeaders(url);
    headers = this.cleanUpHeaders(headers);
    return headers
  }

  static isBetaVersion() {
    return (HttpProvider.minVersion == HttpProvider.betaVersion)
  }

  initAppConfigRegions() {
    if (HttpProvider.isBetaVersion() && APP_CONFIG.regions.indexOf(regions.SN) < 0) {
      APP_CONFIG.regions.push(regions.SN);
    }
  }

  updateFeatureFlags(reason: string, resolutionSource: FlagResolutionSource | null = null) {
    HttpProvider.featureFlags = { ...HttpProvider.featureFlagsCountry, ...HttpProvider.featureFlagsUser };
    let logged = '';
    try {
      logged = JSON.stringify(HttpProvider.featureFlags)
    } catch (ex) {
      logged = 'Failed to stringify feature flags';
    }

    if (HttpProvider.featureFlags[FeatureFlagTypes.BetaTester] && !HttpProvider.isBetaVersion()) {
      LoggerProvider.warning(this, LoggerEvent.BetaTester, `BETA TESTER: updated minVersion from ${HttpProvider.minVersion} to ${HttpProvider.betaVersion}`);
      HttpProvider.minVersion = HttpProvider.betaVersion;
      this.checkTestVersion();
    }
    if (HttpProvider.featureFlags[FeatureFlagTypes.ForceCaptcha] && HttpProvider.disableCaptcha) {
      LoggerProvider.warning(this, LoggerEvent.Captcha, `FORCED CAPTCHA`);
      HttpProvider.disableCaptcha = false;
    }

    if (HttpProvider.featureFlagsLog !== logged) {
      LoggerProvider.info(this, `Current feature flags ${reason}`, HttpProvider.featureFlags);
    }
    HttpProvider.featureFlagsLog = logged;
    this.events.publish(EVENTS_NAME.FEATURE_FLAGS_UPDATED);

    if (resolutionSource)
      this.trackFeatureFlagsResolved(resolutionSource);
  }

  // Reports the flag payload the session runs on — first resolution, then whenever the merged
  // set changes or the session authenticates. Transport and country are reported but kept out
  // of the dedupe key: the post-login session_v2 write and every token refresh re-deliver the
  // same merged set and must stay silent.
  trackFeatureFlagsResolved(resolutionSource: FlagResolutionSource) {
    try {
      if (HttpProvider.featureFlagsResolvedCount >= 50)
        return;
      const { flagProperties, resolvedEntries, flagsEvaluatedCount } = this.buildFlagsResolved();
      const resolvedCountry = resolutionSource === 'signin_token' ? this.getAuthIsoCode() : HttpProvider.featureFlagsCountry.country;
      const resolutionCountry = resolvedCountry || HttpProvider.authCountry || '';
      const identity = HttpProvider.hasAuthCustomerId(null, true) ? 'user' : 'anonymous';

      const resolvedLog = `${identity}|${flagsEvaluatedCount}|${resolvedEntries.join(',')}`;
      if (resolvedLog === HttpProvider.featureFlagsResolvedLog)
        return;
      HttpProvider.featureFlagsResolvedLog = resolvedLog;
      HttpProvider.featureFlagsResolvedCount++;

      const tracked = SegmentProvider.initializedPromise.then(async () => this.analytics.trackEvent({
        eventName: AnalyticsEventType.FeatureFlagsResolved,
        analyticsData: {
          ...flagProperties,
          resolutionSource,
          evaluationSource: 'posthog',
          resolutionCountry,
          flagsCount: resolvedEntries.length,
          flagsEvaluatedCount,
          product: 'App',
        },
      }));
      if (tracked && tracked.catch) {
        tracked.catch(() => {
          if (HttpProvider.featureFlagsResolvedLog === resolvedLog) {
            HttpProvider.featureFlagsResolvedLog = '';
            HttpProvider.featureFlagsResolvedCount--;
          }
        });
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to track feature flags resolved');
    }
  }

  private buildFlagsResolved() {
    const flags = HttpProvider.featureFlags || {};
    const flagProperties: { [key: string]: string } = {};
    const resolvedEntries: string[] = [];
    let flagsEvaluatedCount = 0;
    for (const flag of Object.keys(flags).sort()) {
      if (flag === 'country')
        continue;
      const raw = flags[flag];
      if (raw === undefined || raw === null)
        continue;
      flagsEvaluatedCount++;
      const value = this.formatFlagValue(raw);
      if (value === null)
        continue;
      flagProperties[`ff_${flag}`] = value;
      resolvedEntries.push(`${flag}:${value}`);
    }
    return { flagProperties, resolvedEntries, flagsEvaluatedCount };
  }

  // Null means excluded: defaults (false / 0) and anything that isn't a short scalar state —
  // objects, arrays and remote-config payloads never go to analytics.
  private formatFlagValue(raw): string | null {
    if (typeof raw === 'object')
      return null;
    const value = raw.toString();
    const lower = value.trim().toLowerCase();
    if (lower === '' || lower === 'false' || lower === '0')
      return null;
    if (lower === 'true')
      return 'true';
    if (value.length > 64 || lower.charAt(0) === '[' || lower.charAt(0) === '{')
      return null;
    return value;
  }

  checkTestVersion() {
    if (!HttpProvider.isTestVersion && this.isTestVersion()) {
      HttpProvider.isTestVersion = true;
      HttpProvider.sendLogExtra = ` (${HttpProvider.minVersion}) `;
      LoggerProvider.info(this, '!!! Test version !!!');
      const versionNotice = document.createElement('div');
      versionNotice.className = 'version-notice',
      versionNotice.innerHTML = `Pointing to ${HttpProvider.minVersion}`;
      document.body.appendChild(versionNotice);
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/http/http.ts